import React, { useEffect, useState, useCallback } from 'react';
import {
  PiX,
  PiSealCheck,
  PiCheck,
  PiXCircle,
  PiArrowClockwise,
  PiFileText,
  PiWarningCircle,
} from 'react-icons/pi';
import {
  getRestaurantDocuments,
  reviewRestaurantDocument,
  certifyRestaurant,
  decertifyRestaurant,
} from '../../../services/adminApi';

const DOCUMENT_TYPE_LABELS = {
  kbis: 'KBIS / immatriculation',
  id_card: "Pièce d'identité",
  health_certificate: 'Certificat sanitaire',
  insurance: 'Assurance',
  other: 'Autre document',
};

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-900 ring-1 ring-amber-200/80',
  approved: 'bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200/80',
  rejected: 'bg-rose-50 text-rose-900 ring-1 ring-rose-200/80',
};

const STATUS_LABELS = {
  pending: 'En attente',
  approved: 'Approuvé',
  rejected: 'Rejeté',
};

const RestaurantDocumentsModal = ({ isOpen, restaurant, onClose, onCertificationChange }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [isCertified, setIsCertified] = useState(false);

  const fetchDocuments = useCallback(async () => {
    if (!restaurant) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getRestaurantDocuments(restaurant.id);
      setDocuments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des documents');
    } finally {
      setLoading(false);
    }
  }, [restaurant]);

  useEffect(() => {
    if (isOpen && restaurant) {
      setIsCertified(Boolean(restaurant.is_certified));
      fetchDocuments();
    }
  }, [isOpen, restaurant, fetchDocuments]);

  if (!isOpen || !restaurant) return null;

  const handleApprove = async (documentId) => {
    setActionLoading((prev) => ({ ...prev, [documentId]: true }));
    try {
      await reviewRestaurantDocument(restaurant.id, documentId, { status: 'approved' });
      await fetchDocuments();
    } catch (err) {
      alert(`Erreur: ${err.message}`);
    } finally {
      setActionLoading((prev) => ({ ...prev, [documentId]: false }));
    }
  };

  const handleReject = async (documentId) => {
    const reason = window.prompt('Motif du rejet (visible par le restaurant) :', '');
    if (reason === null) return;
    if (!reason.trim()) {
      alert('Un motif de rejet est requis');
      return;
    }
    setActionLoading((prev) => ({ ...prev, [documentId]: true }));
    try {
      await reviewRestaurantDocument(restaurant.id, documentId, {
        status: 'rejected',
        rejection_reason: reason.trim(),
      });
      await fetchDocuments();
    } catch (err) {
      alert(`Erreur: ${err.message}`);
    } finally {
      setActionLoading((prev) => ({ ...prev, [documentId]: false }));
    }
  };

  const handleCertify = async () => {
    if (!window.confirm(`Certifier ${restaurant.name} ? Le badge sera visible publiquement.`)) return;
    setActionLoading((prev) => ({ ...prev, certify: true }));
    try {
      await certifyRestaurant(restaurant.id);
      setIsCertified(true);
      onCertificationChange?.();
    } catch (err) {
      alert(`Erreur: ${err.message}`);
    } finally {
      setActionLoading((prev) => ({ ...prev, certify: false }));
    }
  };

  const handleDecertify = async () => {
    if (!window.confirm(`Retirer le badge de certification de ${restaurant.name} ?`)) return;
    setActionLoading((prev) => ({ ...prev, certify: true }));
    try {
      await decertifyRestaurant(restaurant.id);
      setIsCertified(false);
      onCertificationChange?.();
    } catch (err) {
      alert(`Erreur: ${err.message}`);
    } finally {
      setActionLoading((prev) => ({ ...prev, certify: false }));
    }
  };

  const hasApprovedDocument = documents.some((d) => d.status === 'approved');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white rounded-2xl p-6 flex flex-col gap-5 max-h-[85vh] overflow-y-auto">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                Documents de certification
                {isCertified && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200/80">
                    <PiSealCheck className="w-3.5 h-3.5" /> Certifié
                  </span>
                )}
              </h2>
              <p className="text-sm text-gray-600">{restaurant.name}</p>
            </div>
            <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-600">
              <PiX className="w-6 h-6" />
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
              <PiWarningCircle className="w-5 h-5 shrink-0" />
              {error}
            </div>
          )}

          {loading ? (
            <div className="py-10 flex justify-center">
              <PiArrowClockwise className="w-6 h-6 animate-spin text-stone-400" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <PiFileText className="w-10 h-10 mx-auto mb-2 text-stone-300" />
              Aucun document envoyé par ce restaurant pour le moment.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {documents.map((doc) => (
                <div key={doc.id} className="border border-stone-200 rounded-xl p-4 flex flex-col gap-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <PiFileText className="w-5 h-5 text-stone-400 shrink-0" />
                      <span className="font-medium text-gray-900">
                        {DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type}
                      </span>
                    </div>
                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${STATUS_STYLES[doc.status] || STATUS_STYLES.pending}`}>
                      {STATUS_LABELS[doc.status] || doc.status}
                    </span>
                  </div>

                  <a
                    href={doc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[rgb(5,70,37)] hover:underline w-fit"
                  >
                    Voir le document
                  </a>

                  {doc.status === 'rejected' && doc.rejection_reason && (
                    <p className="text-sm text-rose-700">Motif du rejet : {doc.rejection_reason}</p>
                  )}

                  {doc.status === 'pending' && (
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => handleApprove(doc.id)}
                        disabled={actionLoading[doc.id]}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-emerald-50 text-emerald-900 hover:bg-emerald-100 disabled:opacity-50"
                      >
                        <PiCheck className="w-4 h-4" /> Approuver
                      </button>
                      <button
                        type="button"
                        onClick={() => handleReject(doc.id)}
                        disabled={actionLoading[doc.id]}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-rose-50 text-rose-900 hover:bg-rose-100 disabled:opacity-50"
                      >
                        <PiXCircle className="w-4 h-4" /> Rejeter
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-stone-100">
            {isCertified ? (
              <button
                type="button"
                onClick={handleDecertify}
                disabled={actionLoading.certify}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-50 text-rose-900 hover:bg-rose-100 disabled:opacity-50"
              >
                Retirer la certification
              </button>
            ) : (
              <button
                type="button"
                onClick={handleCertify}
                disabled={actionLoading.certify || !hasApprovedDocument}
                title={!hasApprovedDocument ? 'Approuvez au moins un document avant de certifier' : undefined}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white hover:opacity-95 disabled:opacity-40"
                style={{ backgroundColor: 'rgb(5, 70, 37)' }}
              >
                <PiSealCheck className="w-5 h-5" /> Certifier le restaurant
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantDocumentsModal;
